import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Alert,
  Button,
  Checkbox,
  Details,
  Field,
  Heading,
  Label,
  Link,
  Paragraph,
  Select,
  Skeleton,
  Table,
  Tag,
  Textarea,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { CheckmarkIcon, ChevronRightIcon } from '@navikt/aksel-icons';
import { downloadProtocolPdf, type ProtocolView } from '../../../api/safetyRounds';
import { useGetUserinfo } from '../../../hooks/queries/useGetUserinfo';
import { UserRoles } from '../../../api/queries';
import {
  useProtocolQuery,
  useRequestChangeMutation,
  useSignProtocolMutation,
} from '../../../hooks/queries/safetyRounds/useRoundQueries';
import { longDate, managerPlanPath, participantsText, planPath } from '../Plans/plansView';
import { problemMessage, roundPathFor } from '../Round/roundView';
import { deviationPath } from '../Deviations/deviationView';
import { DeviationChangeFields } from './DeviationChangeFields';
import {
  DEVIATION_COLORS,
  latestChangeRequest,
  protocolCrumbs,
  protocolFileName,
  protocolState,
  resultParts,
  signingSteps,
  STATE_COLORS,
} from './protocolView';
import { LoadProblem } from '../LoadProblem';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from './Protocol.module.css';

declare global {
  interface Window {
    __SAFETY_ROUND_PROTOCOL_READY__?: boolean;
  }
}

const K = 'safetyRounds.protocol';

export const ProtocolPage = (): ReactElement => {
  const { t } = useTranslation();
  const id = Number(useParams().roundId);
  const [params] = useSearchParams();
  const forPdf = params.get('pdf') === 'true';
  const { data, isLoading, error } = useProtocolQuery(id, forPdf ? (params.get('language') ?? undefined) : undefined);

  // The PDF export waits for this, so every part is open and loaded
  useEffect(() => {
    if (forPdf && data) window.__SAFETY_ROUND_PROTOCOL_READY__ = true;
  }, [forPdf, data]);

  if (isLoading)
    return (
      <div aria-busy="true" className={layout.loading}>
        <span className="ds-sr-only">{t(`${K}.loading`)}</span>
        <Skeleton variant="rectangle" width="100%" height={480} />
      </div>
    );
  if (error || !data) return <LoadProblem error={error} subject="round" />;
  return <ProtocolDocument view={data} forPdf={forPdf} />;
};

/** The checkpoints without an answer, which must be answered before the safety representative signs */
const unansweredOf = (view: ProtocolView) =>
  view.protocol.categories.reduce((sum, category) => sum + category.items.filter((item) => !item.result).length, 0);

/**
 * The protocol as one document: who signs and where it stands, the result, the deviations and comments, every
 * checkpoint, and the signing itself. The PDF is the same page without the parts that are only for the screen.
 */
const ProtocolDocument = ({ view, forPdf }: { view: ProtocolView; forPdf: boolean }): ReactElement => {
  const { t, i18n } = useTranslation();
  const { protocol } = view;
  // The protocol is written in its own language, which the PDF may ask for
  const language = protocol.language === 'en' ? 'en' : 'no';
  const tp = i18n.getFixedT(language);
  const change = latestChangeRequest(protocol);
  const state = protocolState({ ...view, readerIsSafetyRep: view.readerIsSafetyRep ?? false });
  const crumbs = protocolCrumbs(useGetUserinfo().data?.userRoles ?? [], useLocation().pathname, view.planId);

  return (
    <div className={layout.page} data-size="md" data-safety-round-protocol>
      <div className={classes.document}>
        {!forPdf && (
          <nav aria-label={t(`${K}.breadcrumbs`)} className={classes.breadcrumbs} data-print="false">
            <Link asChild data-size="sm">
              <RouterLink to={crumbs.home}>{t(`${K}.crumbs.${crumbs.homeLabel}`)}</RouterLink>
            </Link>
            <ChevronRightIcon aria-hidden="true" />
            {crumbs.plan ? (
              <Link asChild data-size="sm">
                <RouterLink to={crumbs.plan}>{protocol.planName}</RouterLink>
              </Link>
            ) : (
              <span className={classes.crumb}>{protocol.planName}</span>
            )}
            <ChevronRightIcon aria-hidden="true" />
            <span className={classes.crumb} aria-current="page">
              {t(`${K}.crumbs.protocol`)}
            </span>
          </nav>
        )}

        <header className={classes.header}>
          <div className={classes.headerTop}>
            <span className={layout.eyebrow}>{tp(`${K}.eyebrow`)}</span>
            <Tag data-color={STATE_COLORS[state]} data-size="sm" className={classes.state}>
              {forPdf ? tp(`${K}.state.${state === 'SIGNED' ? 'SIGNED' : 'notSigned'}`) : t(`${K}.state.${state}`)}
            </Tag>
          </div>
          <Heading level={1} data-size="lg">
            {protocol.planName}
          </Heading>
        </header>

        {!forPdf && change && view.status === 'IN_PROGRESS' && (
          <Alert data-color="warning" data-size="sm" data-print="false">
            <Heading level={2} data-size="2xs">
              {t(`${K}.changeRequested`)}
            </Heading>
            <Paragraph data-size="sm">«{change.comment}»</Paragraph>
          </Alert>
        )}

        <Facts view={view} />
        <Signing view={view} forPdf={forPdf} />
        <Result view={view} forPdf={forPdf} />
        <Deviations view={view} forPdf={forPdf} />

        {protocol.notChecked.length > 0 && (
          <section aria-labelledby="protocol-not-checked" className={`${layout.card} ${classes.card}`}>
            <div className={classes.cardHead}>
              <Heading level={2} data-size="xs" id="protocol-not-checked">
                {tp(`${K}.notChecked`)}
              </Heading>
              <Paragraph data-size="sm" className={layout.subtle}>
                {tp(`${K}.notCheckedIntro`)}
              </Paragraph>
            </div>
            <ul className={classes.list}>
              {protocol.notChecked.map((item) => (
                <li key={item.number}>
                  <span className={classes.itemTitle}>
                    {item.number} {item.text}
                  </span>
                  {item.reason && <Paragraph data-size="sm">{item.reason}</Paragraph>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {protocol.comments.length > 0 && (
          <section aria-labelledby="protocol-comments" className={`${layout.card} ${classes.card}`}>
            <div className={classes.cardHead}>
              <Heading level={2} data-size="xs" id="protocol-comments">
                {tp(`${K}.comments`)}
              </Heading>
            </div>
            <ul className={classes.list}>
              {protocol.comments.map((item) => (
                <li key={item.number}>
                  <span className={classes.itemTitle}>
                    {item.number} {item.text}
                  </span>
                  <Paragraph data-size="sm" className={layout.subtle}>
                    {item.comment}
                  </Paragraph>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section aria-labelledby="protocol-all" className={`${layout.card} ${classes.card}`}>
          <div className={classes.cardHead}>
            <Heading level={2} data-size="xs" id="protocol-all">
              {tp(`${K}.all`)}
            </Heading>
          </div>
          <div className={classes.categories}>
            {protocol.categories.map((category) => (
              <CategoryDetails key={category.number} forPdf={forPdf}>
                <Details.Summary>
                  {category.number}. {category.name}
                </Details.Summary>
                <Details.Content>
                  <div className={layout.tableScroll}>
                    <Table data-size="sm" className={classes.table}>
                      <Table.Body>
                        {category.items.map((item) => (
                          <Table.Row key={item.itemId}>
                            <Table.Cell className={classes.numberCell}>{item.number}</Table.Cell>
                            <Table.Cell>
                              {item.text}
                              {item.comment && (
                                <Paragraph data-size="xs" className={layout.subtle}>
                                  {item.comment}
                                </Paragraph>
                              )}
                            </Table.Cell>
                            <Table.Cell className={classes.resultCell} data-result={item.result ?? 'none'}>
                              {item.result ? tp(`safetyRounds.round.results.${item.result}`) : tp(`${K}.unanswered`)}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                </Details.Content>
              </CategoryDetails>
            ))}
          </div>
        </section>

        {!forPdf && <SignCard view={view} unanswered={unansweredOf(view)} />}

        <Paragraph data-size="xs" className={layout.subtle}>
          {tp(`${K}.footer`, { name: protocol.templateName, version: protocol.templateVersion })}
        </Paragraph>
      </div>
    </div>
  );
};

/** Closed on the screen, open in the PDF so every checkpoint is in it */
const CategoryDetails = ({ forPdf, children }: { forPdf: boolean; children: ReactNode }): ReactElement => {
  const [open, setOpen] = useState(false);
  return (
    <Details
      data-size="sm"
      open={forPdf || open}
      onToggle={(event) => setOpen((event.currentTarget as HTMLDetailsElement).open)}
    >
      {children}
    </Details>
  );
};

/** The protocol's own language, for what is part of the document */
const useProtocolT = (view: ProtocolView) => {
  const { i18n } = useTranslation();
  const language = view.protocol.language === 'en' ? 'en' : 'no';
  return { tp: i18n.getFixedT(language), language };
};

/** When, what, and who signs */
const Facts = ({ view }: { view: ProtocolView }): ReactElement => {
  const { tp, language } = useProtocolT(view);
  const { protocol } = view;
  const facts = [
    { key: 'date', value: longDate(protocol.completedOn ?? protocol.plannedDate, language) },
    {
      key: 'template',
      value:
        protocol.templateVersion === null
          ? protocol.templateName
          : tp(`${K}.facts.templateValue`, { name: protocol.templateName, version: protocol.templateVersion }),
    },
    { key: 'safetyRep', value: view.safetyRepEmail ?? tp(`${K}.signing.safetyRep.unknown`) },
    { key: 'generalManager', value: view.generalManagerEmail ?? tp(`${K}.signing.generalManager.unknown`) },
    ...(protocol.area && protocol.area !== protocol.planName ? [{ key: 'area', value: protocol.area }] : []),
    ...(protocol.participants.length > 0
      ? [{ key: 'participants', value: participantsText(protocol.participants, tp) }]
      : []),
  ];
  return (
    <section aria-label={tp(`${K}.facts.title`)} className={`${layout.card} ${classes.facts}`}>
      <dl>
        {facts.map((fact) => (
          <div key={fact.key}>
            <dt>{tp(`${K}.facts.${fact.key}`)}</dt>
            <dd>{fact.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

/** Who signs a step: «Deg, …» to the reader on the screen, the email in the PDF */
const stepText = (
  view: ProtocolView,
  step: ReturnType<typeof signingSteps>[number],
  tp: TFunction,
  language: string,
  you: boolean,
) => {
  const S = `${K}.signing`;
  if (step.key === 'deviations') {
    const count = view.protocol.deviations.length;
    if (count === 0) return tp(`${S}.deviations.none`);
    return tp(step.state === 'done' ? `${S}.deviations.sent` : `${S}.deviations.pending`, { count });
  }
  const isReader = you && (step.key === 'safetyRep' ? view.readerIsSafetyRep : view.readerIsGeneralManager);
  const email = step.key === 'safetyRep' ? view.safetyRepEmail : (view.generalManagerEmail ?? null);
  if (step.state === 'done' && step.date)
    return isReader
      ? tp(`${S}.signedByYou`, { date: longDate(step.date, language) })
      : tp(`${S}.signedBy`, { date: longDate(step.date, language), name: email ?? tp(`${S}.${step.key}.unknown`) });
  if (isReader && email) return tp(`${S}.you`, { name: email });
  // The general managers who can sign have been told
  if (step.key === 'generalManager' && step.state === 'current')
    return email ? tp(`${S}.notified`, { name: email }) : tp(`${S}.generalManager.notified`);
  return email ?? tp(`${S}.${step.key}.unknown`);
};

/** «Signering» as three steps: the safety representative, the general manager, and the deviations going out */
const Signing = ({ view, forPdf }: { view: ProtocolView; forPdf: boolean }): ReactElement => {
  const { t } = useTranslation();
  const { tp, language } = useProtocolT(view);
  const S = `${K}.signing`;
  const steps = signingSteps(view);
  return (
    <section aria-labelledby="protocol-signing" className={classes.signing}>
      <Heading level={2} data-size="xs" id="protocol-signing">
        {tp(`${S}.title`)}
      </Heading>
      <ol className={classes.steps}>
        {steps.map((step, index) => (
          <li key={step.key} data-state={step.state} aria-current={step.state === 'current' ? 'step' : undefined}>
            <span className={classes.stepNumber} aria-hidden="true">
              {step.state === 'done' ? <CheckmarkIcon /> : index + 1}
            </span>
            <span className={classes.stepText}>
              <span className={classes.stepTitle}>
                {tp(step.key === 'deviations' && step.state === 'done' ? `${S}.deviations.titleDone` : `${S}.${step.key}.title`)}
                {step.state !== 'next' && <span className="ds-sr-only"> ({tp(`${S}.${step.state}`)})</span>}
              </span>
              <span className={classes.stepDetail}>{stepText(view, step, tp, language, !forPdf)}</span>
            </span>
          </li>
        ))}
      </ol>
      {!forPdf && view.generalManagerMissing && (
        <Alert data-color="warning" data-size="sm" data-print="false">
          <Paragraph data-size="sm">
            {t(view.generalManagerIsSafetyRep ? `${K}.next.generalManagerIsSafetyRep` : `${K}.next.noGeneralManager`)}
          </Paragraph>
        </Alert>
      )}
    </section>
  );
};

/** How the checkpoints were answered, as one bar, and what must be answered before signing */
const Result = ({ view, forPdf }: { view: ProtocolView; forPdf: boolean }): ReactElement => {
  const { t } = useTranslation();
  const { tp } = useProtocolT(view);
  const { counts } = view.protocol;
  const parts = resultParts(counts);
  const unanswered = unansweredOf(view);
  const roles = useGetUserinfo().data?.userRoles ?? [];
  const { pathname } = useLocation();
  const beforeSigning = view.status === 'PLANNED' || view.status === 'IN_PROGRESS';
  const reader = view.readerIsSafetyRep ? 'you' : 'others';
  const warning =
    !forPdf && beforeSigning
      ? unanswered > 0
        ? t(`${K}.result.unanswered.${reader}`, { count: unanswered })
        : view.problems.length > 0 && view.readerIsSafetyRep
          ? t(`${K}.result.missing`, { count: view.problems.length })
          : null
      : null;
  return (
    <section aria-labelledby="protocol-result" className={`${layout.card} ${classes.card}`}>
      <div className={classes.resultHead}>
        <Heading level={2} data-size="xs" id="protocol-result">
          {tp(`${K}.result.title`)}
        </Heading>
        <span className={layout.subtle}>{tp(`${K}.result.total`, { count: counts.total })}</span>
      </div>
      <div className={classes.bar} aria-hidden="true">
        {parts.map((part) => (
          <span key={part.kind} data-kind={part.kind} style={{ flexGrow: part.count }} />
        ))}
      </div>
      <ul className={classes.legend}>
        {parts.map((part) => (
          <li key={part.kind} data-kind={part.kind}>
            <span className={classes.dot} aria-hidden="true" />
            <span>
              <strong>{part.count}</strong> {tp(`${K}.result.parts.${part.kind}`, { count: part.count })}
            </span>
          </li>
        ))}
      </ul>
      {warning && (
        <Alert data-color="warning" data-size="sm" data-print="false">
          <div className={classes.warning}>
            <Paragraph data-size="sm">{warning}</Paragraph>
            {view.mayEditRound && unanswered === 0 && view.problems.length > 0 && (
              <ul className={classes.missing}>
                {view.problems.map((problem) => (
                  <li key={problem.itemId}>
                    <Link asChild data-size="sm">
                      <RouterLink to={roundPathFor(view.roundId, roles, pathname)} state={{ showProblems: true }}>
                        {problemMessage(problem, t)}
                      </RouterLink>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {view.mayEditRound && (
              <Button asChild data-size="sm" variant="secondary">
                <RouterLink to={roundPathFor(view.roundId, roles, pathname)}>{t(`${K}.backToRound`)}</RouterLink>
              </Button>
            )}
          </div>
        </Alert>
      )}
    </section>
  );
};

/** The deviations of the protocol, which go out to the responsible when the general manager has signed */
const Deviations = ({ view, forPdf }: { view: ProtocolView; forPdf: boolean }): ReactElement => {
  const { tp, language } = useProtocolT(view);
  const { protocol } = view;
  const changes = new Map((view.deviationsToChange ?? []).map((entry) => [entry.itemId, entry]));
  const count = protocol.deviations.length;
  return (
    <section aria-labelledby="protocol-deviations" className={`${layout.card} ${classes.card}`}>
      <div className={classes.cardHead}>
        <Heading level={2} data-size="xs" id="protocol-deviations">
          {count === 0 ? tp(`${K}.deviationsNone`) : tp(`${K}.deviationsTitle`, { count })}
        </Heading>
        <Paragraph data-size="sm" className={layout.subtle}>
          {count === 0
            ? tp(`${K}.noDeviations`)
            : view.locked
              ? tp(`${K}.deviationsSent`)
              : [tp(`${K}.deviationsPending`), !forPdf && changes.size > 0 && tp(`${K}.deviationsEdit`)]
                  .filter(Boolean)
                  .join(' ')}
        </Paragraph>
      </div>
      {count > 0 && (
        <ul className={classes.list}>
          {protocol.deviations.map((deviation) => {
            const change = forPdf ? undefined : changes.get(deviation.itemId);
            const title = `${deviation.number} ${deviation.text}`;
            return (
              <li key={deviation.itemId} className={classes.deviation}>
                <div className={classes.deviationHead}>
                  {deviation.deviationId && !forPdf ? (
                    <Link asChild className={classes.itemTitle}>
                      <RouterLink to={deviationPath(deviation.deviationId)}>{title}</RouterLink>
                    </Link>
                  ) : (
                    <span className={classes.itemTitle}>{title}</span>
                  )}
                  {/* The status follows the case once it has gone out */}
                  {view.locked && deviation.status && (
                    <Tag data-color={DEVIATION_COLORS[deviation.status]} data-size="sm">
                      {tp(`${K}.deviationStatus.${deviation.status}`)}
                    </Tag>
                  )}
                </div>
                {deviation.description && <Paragraph data-size="sm">{deviation.description}</Paragraph>}
                {change ? (
                  <DeviationChangeFields
                    roundId={view.roundId}
                    number={deviation.number}
                    change={change}
                    people={view.people ?? []}
                    labels="visible"
                    className={classes.changeFields}
                    errorClassName={classes.changeError}
                  />
                ) : (
                  <Paragraph data-size="xs" className={layout.subtle}>
                    {[
                      tp(`${K}.responsible`, { name: deviation.responsibleEmail ?? tp(`${K}.defaultResponsible`) }),
                      deviation.dueDate
                        ? tp(`${K}.due`, { date: longDate(deviation.dueDate, language) })
                        : tp(`${K}.defaultDue`),
                    ].join(' · ')}
                  </Paragraph>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

/**
 * «Signer protokollen»: the safety representative confirms and sends it to the general manager, the general manager
 * signs having read it or sends it back, and a signed protocol is downloaded as PDF.
 */
const SignCard = ({ view, unanswered }: { view: ProtocolView; unanswered: number }): ReactElement | null => {
  const { t, i18n } = useTranslation();
  const { protocol } = view;
  const sign = useSignProtocolMutation(view.roundId);
  const change = useRequestChangeMutation(view.roundId);
  const [confirmed, setConfirmed] = useState(false);
  const [tried, setTried] = useState(false);
  const [changing, setChanging] = useState(false);
  const [comment, setComment] = useState('');
  const [commentTried, setCommentTried] = useState(false);
  const [pdfLanguage, setPdfLanguage] = useState(protocol.language);
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const repSignature = protocol.signatures.find((signature) => signature.role === 'SAFETY_REP');
  const roles = useGetUserinfo().data?.userRoles ?? [];
  const { pathname } = useLocation();
  // HR and the general manager choose the safety representative of the plan
  const planLink = roles.includes(UserRoles.GENERAL_MANAGER)
    ? managerPlanPath(view.planId)
    : roles.includes(UserRoles.HR_ADMIN)
      ? planPath(view.planId)
      : null;

  const download = async () => {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      const blob = await downloadProtocolPdf(view.roundId, pdfLanguage);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = protocolFileName(protocol);
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  const failed = (sign.isError || change.isError) && (
    <Alert data-color="danger" data-size="sm">
      <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
    </Alert>
  );

  // The safety representative signs a round in progress, once every checkpoint has its answer
  if (view.status === 'IN_PROGRESS' && view.readerIsSafetyRep) {
    const blocked = view.problems.length > 0;
    return (
      <section aria-labelledby="protocol-sign" className={`${layout.card} ${classes.card}`} data-print="false">
        <Heading level={2} data-size="xs" id="protocol-sign">
          {t(`${K}.sign.title`)}
        </Heading>
        <Paragraph data-size="sm" className={layout.subtle}>
          {t(`${K}.safetyRepSignText`)}
        </Paragraph>
        <div>
          <Checkbox
            data-size="sm"
            label={t(`${K}.sign.confirmWalked`)}
            checked={confirmed}
            aria-invalid={tried && !confirmed ? true : undefined}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          {tried && !confirmed && <ValidationMessage>{t(`${K}.sign.confirmWalkedError`)}</ValidationMessage>}
        </div>
        {blocked && (
          <Paragraph data-size="sm" id="protocol-sign-blocked" className={classes.blocked}>
            {unanswered > 0 ? t(`${K}.sign.blockedUnanswered`) : t(`${K}.sign.blocked`)}
          </Paragraph>
        )}
        <div className={layout.actions}>
          <Button
            data-size="md"
            aria-busy={sign.isPending || undefined}
            aria-disabled={blocked || undefined}
            aria-describedby={blocked ? 'protocol-sign-blocked' : undefined}
            onClick={() => {
              if (blocked) return;
              setTried(true);
              if (confirmed) sign.mutate({ role: 'SAFETY_REP' });
            }}
          >
            {t(`${K}.signAndSend`)}
          </Button>
          <Button asChild data-size="md" variant="secondary">
            <RouterLink to={roundPathFor(view.roundId, roles, pathname)}>{t(`${K}.editAnswers`)}</RouterLink>
          </Button>
        </div>
        {failed}
      </section>
    );
  }

  // Only the safety representative of the plan signs first. Anyone else sees who, and HR or the general manager can change it.
  if (view.status === 'IN_PROGRESS' && !view.readerIsSafetyRep)
    return (
      <section aria-labelledby="protocol-sign" className={`${layout.card} ${classes.card}`} data-print="false">
        <Heading level={2} data-size="xs" id="protocol-sign">
          {t(`${K}.sign.title`)}
        </Heading>
        <Paragraph data-size="sm">
          {view.safetyRepEmail && view.safetyRepWithoutRole
            ? t(`${K}.sign.safetyRepWithoutRole`, { email: view.safetyRepEmail })
            : view.safetyRepEmail
              ? t(`${K}.sign.onlyPlanSafetyRep`, { email: view.safetyRepEmail })
              : t(`${K}.sign.noPlanSafetyRep`)}
        </Paragraph>
        {planLink && (
          <Link asChild data-size="sm">
            <RouterLink to={planLink}>{t(`${K}.sign.changeSafetyRep`)}</RouterLink>
          </Link>
        )}
        {/* The general manager may walk the round too, in their own part of the app */}
        {view.mayEditRound && (
          <div className={layout.actions}>
            <Button asChild data-size="sm" variant="secondary">
              <RouterLink to={roundPathFor(view.roundId, roles, pathname)}>{t(`${K}.editAnswers`)}</RouterLink>
            </Button>
          </div>
        )}
      </section>
    );

  // The safety representative has signed and the protocol waits for the general manager
  if (view.status === 'AWAITING_SIGNATURE' && view.readerIsSafetyRep && !view.maySignAsGeneralManager)
    return (
      <section className={classes.signed} data-print="false" aria-live="polite">
        <CheckmarkIcon aria-hidden="true" className={classes.signedIcon} />
        <Paragraph data-size="sm">
          {[
            repSignature &&
              (view.generalManagerEmail
                ? t(`${K}.sign.youSigned`, {
                    date: longDate(repSignature.signedAt, i18n.language),
                    name: view.generalManagerEmail,
                  })
                : t(`${K}.sign.youSignedUnknown`, { date: longDate(repSignature.signedAt, i18n.language) })),
            // Two independent signatures: the one who signed as safety representative does not sign for the employer
            view.signedAsSafetyRep && t(`${K}.next.signedAsSafetyRep`),
          ]
            .filter(Boolean)
            .join(' ')}
        </Paragraph>
      </section>
    );

  if (view.status === 'AWAITING_SIGNATURE' && view.signedAsSafetyRep)
    return (
      <Alert data-color="info" data-size="sm" data-print="false">
        <Paragraph data-size="sm">{t(`${K}.next.signedAsSafetyRep`)}</Paragraph>
      </Alert>
    );

  if (view.maySignAsGeneralManager)
    return (
      <section aria-labelledby="protocol-sign" className={`${layout.card} ${classes.card}`} data-print="false">
        <Heading level={2} data-size="xs" id="protocol-sign">
          {t(`${K}.sign.title`)}
        </Heading>
        {changing ? (
          <>
            <Field>
              <Label htmlFor="protocol-change">{t(`${K}.changeComment`)}</Label>
              <Paragraph data-size="sm" className={layout.subtle} id="protocol-change-to">
                {view.safetyRepEmail
                  ? t('safetyRounds.tasks.generalManager.changeTo', { name: view.safetyRepEmail })
                  : t('safetyRounds.tasks.generalManager.changeToUnknown')}
              </Paragraph>
              <Textarea
                id="protocol-change"
                rows={3}
                value={comment}
                aria-describedby="protocol-change-to"
                aria-invalid={commentTried && !comment.trim() ? true : undefined}
                onChange={(event) => setComment(event.target.value)}
              />
              {commentTried && !comment.trim() && <ValidationMessage>{t(`${K}.changeCommentError`)}</ValidationMessage>}
            </Field>
            <div className={layout.actions}>
              <Button
                data-size="md"
                aria-busy={change.isPending || undefined}
                onClick={() => {
                  setCommentTried(true);
                  if (comment.trim()) change.mutate(comment.trim(), { onSuccess: () => setChanging(false) });
                }}
              >
                {t(`${K}.sendChange`)}
              </Button>
              <Button data-size="md" variant="secondary" onClick={() => setChanging(false)}>
                {t('safetyRounds.cancel')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <Paragraph data-size="sm" className={layout.subtle}>
              {t(`${K}.managerSignText`)}
            </Paragraph>
            <div>
              <Checkbox
                data-size="sm"
                label={t(`${K}.confirmRead`)}
                checked={confirmed}
                aria-invalid={tried && !confirmed ? true : undefined}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
              {tried && !confirmed && <ValidationMessage>{t(`${K}.confirmReadError`)}</ValidationMessage>}
            </div>
            <div className={layout.actions}>
              <Button
                data-size="md"
                aria-busy={sign.isPending || undefined}
                onClick={() => {
                  setTried(true);
                  if (confirmed) sign.mutate({ role: 'GENERAL_MANAGER', confirmedRead: true });
                }}
              >
                {t(`${K}.signAsManager`)}
              </Button>
              <Button data-size="md" variant="secondary" onClick={() => setChanging(true)}>
                {t(`${K}.requestChange`)}
              </Button>
            </div>
          </>
        )}
        {failed}
      </section>
    );

  if (view.locked)
    return (
      <section aria-labelledby="protocol-download" className={`${layout.card} ${classes.card}`} data-print="false">
        <div className={classes.cardHead}>
          <Heading level={2} data-size="xs" id="protocol-download">
            {t(`${K}.lockedTitle`)}
          </Heading>
          <Paragraph data-size="sm" className={layout.subtle}>
            {t(`${K}.lockedText`)}
          </Paragraph>
        </div>
        <div className={classes.download}>
          {view.languages.length > 1 && (
            <Field>
              <Label htmlFor="protocol-pdf-language" data-size="sm">
                {t(`${K}.pdfLanguage`)}
              </Label>
              <Select
                id="protocol-pdf-language"
                data-size="sm"
                value={pdfLanguage}
                onChange={(event) => setPdfLanguage(event.target.value)}
              >
                {view.languages.map((code) => (
                  <Select.Option key={code} value={code}>
                    {t(`hms.languages.${code}`, { defaultValue: code })}
                  </Select.Option>
                ))}
              </Select>
            </Field>
          )}
          <Button data-size="sm" variant="secondary" aria-busy={downloading || undefined} onClick={download}>
            {t(`${K}.download`)}
          </Button>
        </div>
        {downloadFailed && (
          <Alert data-color="danger" data-size="sm">
            <Paragraph data-size="sm">{t(`${K}.downloadFailed`)}</Paragraph>
          </Alert>
        )}
      </section>
    );

  return null;
};
